import type React from "react"
import { useTranslation } from "react-i18next"

import { DateRangeField } from "@/components/fields/date-range-field"
import { DateTime } from "luxon"

import type { DateRange } from "@mui/x-date-pickers-pro/models"

import { inclusiveRangeEndingOn } from "./site-bills-range"

const DATE_RANGE_SHORTCUTS = [
  { key: "past1Month", offset: { months: 1 } },
  { key: "past3Months", offset: { months: 3 } },
  { key: "past1Year", offset: { years: 1 } },
] as const

interface SiteBillsDateRangeProps {
  anchorEnd: string
  end: string
  onDateRangeChange: (range: DateRange<DateTime>) => void
  start: string
}

const toPickerDate = (value: string): DateTime =>
  DateTime.fromISO(value).startOf("day")

export const SiteBillsDateRange: React.FC<SiteBillsDateRangeProps> = ({
  anchorEnd,
  end,
  onDateRangeChange,
  start,
}) => {
  const { t } = useTranslation("sites")
  const shortcutItems = DATE_RANGE_SHORTCUTS.flatMap((shortcut) => {
    const range = inclusiveRangeEndingOn(anchorEnd, shortcut.offset)
    const rangeStart = range?.[0]
    const rangeEnd = range?.[1]
    if (!rangeStart?.isValid || !rangeEnd?.isValid) {
      return []
    }

    return [
      {
        getValue: (): DateRange<DateTime> => [rangeStart, rangeEnd],
        label: t(`intervalData.dateRangeShortcuts.${shortcut.key}`),
      },
    ]
  })

  return (
    <DateRangeField
      onChange={onDateRangeChange}
      shortcuts={shortcutItems}
      value={[toPickerDate(start), toPickerDate(end)]}
    />
  )
}
